// PlaybookItemFocusAttachments.jsx

import React, { useCallback } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Utilities
import {
  getWorkflowStepAttachmentPresentation,
  partitionWorkflowStepAttachments,
} from 'node/missions/mission/utils/workflowStepAttachmentUtils';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import 'node/missions/mission/task/attachments/NodeMissionTaskAttachments.css';
import './PlaybookItemFocusAttachments.css';

// Components
import Icon from 'svg/Icon';
import NodeMissionTaskAttachmentPhotoThumb from 'node/missions/mission/task/attachments/NodeMissionTaskAttachmentPhotoThumb';

/**
 * PlaybookItemFocusAttachments
 *
 * Item-scoped attachments for the checklist-item wizard.
 *
 * @param {object[]} [attachments]
 * @returns {ReactNode}
 */
const PlaybookItemFocusAttachments = React.memo(({
  attachments = [],
}) => {
  const { theme } = useTheme();
  const { getType } = useNodeTypes();
  const { openNode } = useNodeOpenIntent();

  const { photos, sketches, others } = partitionWorkflowStepAttachments(attachments);

  const handleOpenAttachment = useCallback((node) => {
    if (!node?.key) return;
    openNode(node, { preferPeek: true });
  }, [openNode]);

  if (!attachments.length) return null;

  return (
    <div className="playbook-item-focus-attachments">
      {photos.length > 0 ? (
        <div className="playbook-item-focus-attachments-photo-grid">
          {photos.map((node) => (
            <NodeMissionTaskAttachmentPhotoThumb
              key={node.key}
              node={node}
              onClick={handleOpenAttachment}
            />
          ))}
        </div>
      ) : null}

      {sketches.length > 0 ? (
        <div className="playbook-item-focus-attachments-photo-grid">
          {sketches.map((node) => (
            <NodeMissionTaskAttachmentPhotoThumb
              key={node.key}
              node={node}
              onClick={handleOpenAttachment}
            />
          ))}
        </div>
      ) : null}

      {others.map((attachment) => {
        const { title, snippet } = getWorkflowStepAttachmentPresentation(attachment);
        const titleAttr = snippet ? `${title} — ${snippet}` : title;
        const typeDef = getType(attachment?.type) || null;
        const { iconKey, iconPath } = resolveNodeIcon({
          node: attachment,
          typeDef,
        });

        return (
          <button
            key={attachment.key}
            className="playbook-item-focus-attachments-item"
            style={{ color: theme.foreground }}
            title={titleAttr}
            type="button"
            onClick={() => handleOpenAttachment(attachment)}
          >
            <span className="playbook-item-focus-attachments-item-icon">
              {iconPath ? (
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  path={iconPath}
                  width={16}
                />
              ) : (
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey={iconKey}
                  width={16}
                />
              )}
            </span>
            <span className="playbook-item-focus-attachments-item-label">
              <span className="playbook-item-focus-attachments-item-title">
                {title}
              </span>
              {snippet ? (
                <span
                  className="playbook-item-focus-attachments-item-snippet"
                  style={{ color: theme.foregroundMuted }}
                >
                  {snippet}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
});

PlaybookItemFocusAttachments.displayName = 'PlaybookItemFocusAttachments';

export default PlaybookItemFocusAttachments;
